import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { useNavigate } from "react-router-dom";
import CategorySelector from "@/components/report/CategorySelector";
import LocationInput from "@/components/report/LocationInput";
import PhotoUpload from "@/components/report/PhotoUpload"; 
import { useToast } from "@/components/ui/use-toast";
import { motion } from "framer-motion";
import { getCategories } from "@/utils/categories";
import { Camera, Send, UserCheck, UserX } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { z } from "zod";

// Schéma de validation avec zod
const reportSchema = z.object({
  title: z.string()
    .trim()
    .min(5, { message: "Le titre doit contenir au moins 5 caractères" })
    .max(100, { message: "Le titre ne peut pas dépasser 100 caractères" }),
  description: z.string()
    .trim()
    .min(20, { message: "La description doit contenir au moins 20 caractères" })
    .max(2000, { message: "La description ne peut pas dépasser 2000 caractères" }),
  category: z.string().min(1, { message: "La catégorie est obligatoire" }),
  location: z.string()
    .trim()
    .min(3, { message: "La localisation est obligatoire" })
    .max(200, { message: "La localisation ne peut pas dépasser 200 caractères" }),
});

const CreateReport = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [location, setLocation] = useState("");
  const [category, setCategory] = useState("");
  const [photos, setPhotos] = useState<(File | string)[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [step, setStep] = useState(1);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isAnonymous, setIsAnonymous] = useState(true); // Par défaut anonyme

  // Gérer la navigation entre les étapes
  const nextStep = () => {
    const newErrors: Record<string, string> = {};

    // Validation selon l'étape actuelle
    if (step === 1) {
      if (!title.trim()) newErrors.title = "Le titre est obligatoire";
      if (!description.trim()) newErrors.description = "La description est obligatoire";
      if (!category) newErrors.category = "La catégorie est obligatoire";
    } else if (step === 2) {
      if (!location.trim()) newErrors.location = "La localisation est obligatoire";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    if (step < 3) setStep(step + 1);
  };

  const prevStep = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Validation des données avec zod
      const validatedData = reportSchema.parse({
        title,
        description,
        category,
        location
      });
      
      // Vérifier que l'utilisateur est connecté
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session?.user) {
        toast({
          title: "Connexion requise",
          description: "Vous devez être connecté pour créer un signalement",
          variant: "destructive",
        });
        navigate("/auth");
        return;
      }
      
      // Insérer le signalement dans Supabase
      const { data: insertedReport, error: insertError } = await (supabase.from as any)('reports')
        .insert([{
          title: validatedData.title,
          description: validatedData.description,
          category: validatedData.category,
          location: validatedData.location,
          user_id: session.user.id,
          is_anonymous: isAnonymous,
          status: 'pending',
        }])
        .select('id')
        .single();
      
      if (insertError) {
        throw insertError;
      }

      // Upload photos si présentes
      const filePhotos = photos.filter((p): p is File => p instanceof File);
      if (filePhotos.length > 0 && insertedReport) {
        const { uploadReportPhotos } = await import("@/utils/reportPhotos");
        await uploadReportPhotos(insertedReport.id, session.user.id, filePhotos);
      }
      
      toast({
        title: "Signalement envoyé !",
        description: "Votre signalement a été enregistré avec succès.",
      });
      
      navigate("/");
    } catch (error) {
      if (error instanceof z.ZodError) {
        // Afficher les erreurs de validation
        const newErrors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path[0]) {
            newErrors[err.path[0] as string] = err.message;
          }
        });
        setErrors(newErrors);
        
        toast({
          title: "Erreur de validation",
          description: "Veuillez corriger les erreurs dans le formulaire",
          variant: "destructive",
        });
      } else if (error instanceof Error) {
        console.error("Erreur lors de la création du signalement:", error);
        toast({
          title: "Erreur",
          description: error.message || "Une erreur est survenue lors de l'envoi du signalement",
          variant: "destructive",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Animation des transitions
  const fadeInVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
  };

  return (
    <div className="bg-white dark:bg-gray-900 shadow-lg rounded-lg overflow-hidden">
      <div className="p-6">
        <h2 className="text-2xl font-bold mb-6 text-center">Créer un nouveau signalement</h2>
        
        <div className="flex justify-between mb-8">
          {[1, 2, 3].map((item) => (
            <div 
              key={item}
              className={`flex-1 relative ${item > 1 ? "ml-4" : ""}`}
            >
              <div
                className={`h-2 rounded-full ${
                  step >= item ? "bg-primary" : "bg-gray-200 dark:bg-gray-700"
                }`}
              ></div>
              <div className="mt-2 text-center text-sm">
                {item === 1 && "Informations"}
                {item === 2 && "Localisation"}
                {item === 3 && "Photos"}
              </div>
            </div>
          ))}
        </div>
        
        <form onSubmit={handleSubmit}>
          {step === 1 && (
            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeInVariants}
              className="space-y-4"
            >
              <div>
                <label htmlFor="title" className="block mb-1 font-medium">
                  Titre du signalement
                </label>
                <Input
                  id="title"
                  placeholder="Ex: Nid de poule dangereux"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className={errors.title ? "border-red-500" : ""}
                />
                {errors.title && (
                  <p className="mt-1 text-sm text-red-500">{errors.title}</p>
                )}
              </div>
              
              <div>
                <label htmlFor="description" className="block mb-1 font-medium">
                  Description
                </label>
                <Textarea
                  id="description"
                  placeholder="Décrivez le problème en détail..."
                  rows={4}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className={errors.description ? "border-red-500" : ""}
                />
                {errors.description && (
                  <p className="mt-1 text-sm text-red-500">{errors.description}</p>
                )}
              </div>
              
              <div>
                <CategorySelector
                  value={category}
                  onChange={setCategory}
                  error={errors.category}
                />
              </div>
            </motion.div>
          )}
          
          {step === 2 && (
            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeInVariants}
              className="space-y-4"
            >
              <div className="mb-6">
                <LocationInput
                  value={location}
                  onChange={setLocation}
                  error={errors.location}
                />
              </div>
            </motion.div>
          )}
          
          {step === 3 && (
            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeInVariants}
              className="space-y-6"
            >
              <div>
                <h3 className="text-lg font-medium mb-2 flex items-center">
                  <Camera className="mr-2 h-5 w-5" />
                  Photos (optionnel)
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                  Ajoutez des photos pour aider à comprendre le problème.
                </p>
                
                <PhotoUpload 
                  value={photos}
                  onChange={setPhotos}
                />
              </div>
              
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-4 rounded-lg mb-6">
                <div className="flex items-start space-x-3">
                  <Checkbox 
                    id="anonymous" 
                    checked={isAnonymous}
                    onCheckedChange={(checked) => setIsAnonymous(checked as boolean)}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <label 
                      htmlFor="anonymous" 
                      className="text-sm font-medium cursor-pointer flex items-center gap-2"
                    >
                      {isAnonymous ? (
                        <>
                          <UserX className="h-4 w-4" />
                          Signalement anonyme
                        </>
                      ) : (
                        <>
                          <UserCheck className="h-4 w-4" />
                          Signalement avec identité
                        </>
                      )}
                    </label>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                      {isAnonymous ? (
                        "Votre identité restera confidentielle. Les autres utilisateurs verront 'Citoyen anonyme'."
                      ) : (
                        "En décochant cette option, vous acceptez que votre profil soit visible publiquement sur ce signalement."
                      )}
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
                <h4 className="font-medium mb-2">Récapitulatif du signalement</h4>
                <dl className="grid grid-cols-1 gap-y-2 text-sm">
                  <div className="grid grid-cols-3">
                    <dt className="font-medium text-gray-500 dark:text-gray-400">Titre:</dt>
                    <dd className="col-span-2">{title}</dd>
                  </div>
                  <div className="grid grid-cols-3">
                    <dt className="font-medium text-gray-500 dark:text-gray-400">Catégorie:</dt>
                    <dd className="col-span-2">
                      {category && getCategories().find(cat => cat.id === category)?.label}
                    </dd>
                  </div>
                  <div className="grid grid-cols-3">
                    <dt className="font-medium text-gray-500 dark:text-gray-400">Lieu:</dt>
                    <dd className="col-span-2">{location}</dd>
                  </div>
                  <div className="grid grid-cols-3">
                    <dt className="font-medium text-gray-500 dark:text-gray-400">Visibilité:</dt>
                    <dd className="col-span-2">
                      {isAnonymous ? "Anonyme" : "Avec identité"}
                    </dd>
                  </div>
                </dl>
              </div>
            </motion.div>
          )}
          
          <div className="flex justify-between mt-8">
            {step > 1 ? (
              <Button
                type="button"
                variant="outline"
                onClick={prevStep}
                disabled={isSubmitting}
              >
                Précédent
              </Button>
            ) : (
              <div></div>
            )}
            
            {step < 3 ? (
              <Button type="button" onClick={nextStep}>
                Suivant
              </Button>
            ) : (
              <Button type="submit" className="flex items-center" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <div className="h-4 w-4 mr-2 rounded-full border-2 border-t-transparent border-white animate-spin"></div>
                    Envoi en cours...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Envoyer le signalement
                  </>
                )}
              </Button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateReport;
